import React, { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';

import {
    useGetFeedEventsQuery,
    useGetMyClubsQuery,
    useDeleteEventMutation,
    useSubscribeEventMutation,
    useUnsubscribeEventMutation,
} from '../api/apiSlice';
import { useDecodedToken } from '../app/hooks';
import { FeedEvent } from '../types/api';
import { groupByCalendarDay } from '../utils/dateGrouping';
import EventFormDialog from './EventFormDialog';
import ViewModeToggle, { ViewMode } from './ViewModeToggle';
import DayGroupHeader from './DayGroupHeader';
import FeedCard, { FeedCardMenuAction } from './FeedCard';
import FeedCardSkeleton from './FeedCardSkeleton';

function Events() {
    const { t, i18n } = useTranslation();
    const decodedToken = useDecodedToken();
    const ID = decodedToken?.id ?? -1;
    const { data: eventsList = [], isLoading } = useGetFeedEventsQuery(undefined, { skip: ID === -1 });
    const { data: myClubs = [] } = useGetMyClubsQuery();
    const [deleteEvent] = useDeleteEventMutation();
    const [subscribeEvent] = useSubscribeEventMutation();
    const [unsubscribeEvent] = useUnsubscribeEventMutation();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingEvent, setEditingEvent] = useState<FeedEvent | undefined>(undefined);
    const [errorSnackbarOpen, setErrorSnackbarOpen] = useState(false);
    const [viewMode, setViewMode] = useState<ViewMode>('date');

    const canCreate = myClubs.some((club) => club.ROLE === 'ADMIN' || club.ROLE === 'ANIMATOR');
    // Any admin or animator of the event's own club can edit/delete it, even
    // if that's not the club they're actively viewing.
    const canManage = (eventItem: FeedEvent) =>
        myClubs.some(
            (club) => club.CLUB_ID === eventItem.CLUB_ID && (club.ROLE === 'ADMIN' || club.ROLE === 'ANIMATOR'),
        );

    const dayGroups = useMemo(() => {
        if (viewMode !== 'weekday') return [];
        const locale = i18n.language === 'fr' ? 'fr-FR' : 'en-US';
        return groupByCalendarDay(eventsList, (eventItem) => new Date(eventItem.DATE), locale, t('common.unscheduled'));
    }, [eventsList, viewMode, i18n.language, t]);

    const handleAdd = () => {
        setEditingEvent(undefined);
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setEditingEvent(undefined);
    };

    const handleDelete = async (eventId: number) => {
        try {
            await deleteEvent(eventId).unwrap();
        } catch (error) {
            console.error('Error:', error);
            setErrorSnackbarOpen(true);
        }
    };

    const handleRsvp = async (eventItem: FeedEvent) => {
        try {
            if (eventItem.IS_PARTICIPANT) {
                await unsubscribeEvent({ userID: ID, eventID: eventItem.ID }).unwrap();
            } else {
                await subscribeEvent({ userID: ID, eventID: eventItem.ID }).unwrap();
            }
        } catch (error) {
            console.error('Error:', error);
            setErrorSnackbarOpen(true);
        }
    };

    const renderCard = (eventItem: FeedEvent) => {
        const menuActions: FeedCardMenuAction[] = canManage(eventItem)
            ? [
                  {
                      label: t('common.edit'),
                      icon: <EditIcon fontSize="small" />,
                      onClick: () => {
                          setEditingEvent(eventItem);
                          setDialogOpen(true);
                      },
                  },
                  {
                      label: t('common.delete'),
                      icon: <DeleteIcon fontSize="small" color="error" />,
                      onClick: () => handleDelete(eventItem.ID),
                  },
              ]
            : [];

        return (
            <Grid item xs={12} sm={6} md={4} key={eventItem.ID}>
                <FeedCard
                    image={eventItem.IMAGE}
                    clubName={eventItem.CLUB_NAME}
                    clubLogo={eventItem.CLUB_LOGO}
                    date={eventItem.DATE}
                    title={eventItem.TITLE}
                    subtitle={eventItem.LOCATION ?? undefined}
                    participants={eventItem.PARTICIPANTS}
                    participantLabel={(n) => t('feed.participantCount', { count: n })}
                    isParticipant={Boolean(eventItem.IS_PARTICIPANT)}
                    menuActions={menuActions}
                    primaryAction={
                        eventItem.IS_PARTICIPANT ? (
                            <Button variant="outlined" size="small" color="error" onClick={() => handleRsvp(eventItem)}>
                                {t('events.cancelRsvp')}
                            </Button>
                        ) : (
                            <Button variant="contained" size="small" onClick={() => handleRsvp(eventItem)}>
                                {t('events.rsvp')}
                            </Button>
                        )
                    }
                />
            </Grid>
        );
    };

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
                <Typography variant="h4" gutterBottom>
                    {t('events.title')}
                </Typography>
                {canCreate && (
                    <Button variant="contained" color="primary" onClick={handleAdd}>
                        {t('events.add')}
                    </Button>
                )}
            </Box>

            {!isLoading && eventsList.length > 0 && (
                <Box sx={{ marginBottom: 2 }}>
                    <ViewModeToggle value={viewMode} onChange={setViewMode} />
                </Box>
            )}

            {viewMode === 'weekday' && !isLoading && eventsList.length > 0 ? (
                dayGroups.map((group) => (
                    <div key={group.key}>
                        <DayGroupHeader label={group.label} count={group.items.length} />
                        <Grid container spacing={2}>
                            {group.items.map(renderCard)}
                        </Grid>
                    </div>
                ))
            ) : (
                <Grid container spacing={2}>
                    {isLoading &&
                        Array.from({ length: 3 }).map((_, index) => (
                            <Grid item xs={12} sm={6} md={4} key={`skeleton-${index}`}>
                                <FeedCardSkeleton />
                            </Grid>
                        ))}

                    {!isLoading && eventsList.length === 0 && (
                        <Grid item xs={12}>
                            <Typography color="text.secondary">{t('events.empty')}</Typography>
                        </Grid>
                    )}

                    {!isLoading && eventsList.map(renderCard)}
                </Grid>
            )}

            {dialogOpen && <EventFormDialog event={editingEvent} onClose={handleCloseDialog} />}
            <Snackbar open={errorSnackbarOpen} autoHideDuration={4000} onClose={() => setErrorSnackbarOpen(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={() => setErrorSnackbarOpen(false)} severity="error" variant="filled">
                    {t('common.genericError')}
                </Alert>
            </Snackbar>
        </Container>
    );
}

export default Events;
